import { jwtDecode } from 'jwt-decode';
import {
  createStytchB2BHeadlessClient,
  mount,
  B2BProducts,
  AuthFlowType,
  StytchEventType,
} from '@stytch/vanilla-js/b2b';
import { mountAdminPortalSSO as mountAdminPortalSSOWidget } from '@stytch/vanilla-js/b2b/adminPortal';
// Leaf-only mark (blue-on-white variant): the login page already shows the
// full logo on its blue panel, so the widget shouldn't repeat it. ?url because
// Stytch wants a URL string, not a Vue component.
import auxoLeafUrl from '@/assets/svg/auxo-leaf-blue.svg?url';
import router from '../../router/index';
import { setTokens, clearTokens } from './session';
import { useEnvironment } from '@/composables/useEnvironment';
import { inSlot } from '@/utils/slot';

const AUTH_PROVIDER = import.meta.env.VITE_AUTH_PROVIDER || 'cognito';

// Breakglass method split (CPRD-5849): passwords where they're a daily login
// method (local desk, dev, test); staging/prod widgets offer email magic links
// instead, so no password flow exists where opaque errors are enforced.
// Derived from the build tier (import.meta.env.MODE, set by frontend_build.py
// via --mode) rather than a dedicated env var, so it can never be unset.
const { isLocal, isEng, isTest } = useEnvironment();
const STYTCH_PASSWORDS_ENABLED = isLocal() || isEng() || isTest();

// LoginView keys the breakglass collapse off this — exported so the tier
// split has exactly one definition.
export const stytchPasswordsEnabled = STYTCH_PASSWORDS_ENABLED;

const stytchPublicToken = import.meta.env.VITE_STYTCH_PUBLIC_TOKEN;
const stytchRedirectUri = import.meta.env.VITE_STYTCH_REDIRECT_URI;
// auxo-api routes, so they follow the release slot.
const stytchTokenUrl = inSlot(import.meta.env.VITE_STYTCH_TOKEN_URL);
const stytchSsoConnectionsUrl = inSlot(
  import.meta.env.VITE_STYTCH_SSO_CONNECTIONS_URL
);
const stytchConnectionId = import.meta.env.VITE_STYTCH_SSO_CONNECTION_ID;
// VITE_STYTCH_ORGANIZATION_SLUG is intentionally not read here: the Discovery
// flow resolves the organization from the member's email rather than being
// pinned to one. The Organization-flow variant on 5529-Stytch-OrgFlow does use
// it, and frontend_build.py still emits it for both.
const stytchTrustedAuthProfileId = import.meta.env
  .VITE_STYTCH_TRUSTED_AUTH_PROFILE_ID;
const stytchOrganizationId = import.meta.env.VITE_STYTCH_ORGANIZATION_ID;

// Initialise Stytch B2B client whenever this client has been onboarded to
// Stytch at all — not just when it's the active login provider. Cognito
// clients still need a live client to bootstrap an admin-portal session
// (see mountAdminPortalSSO below).
const stytch = stytchPublicToken
  ? createStytchB2BHeadlessClient(stytchPublicToken)
  : null;

// org_slug → connection_id, emitted by frontend_build.py — resolved live from
// the Stytch API at build time, with /{deployment_id}/stytch/info as topology
// and fallback. Two entries on a mixed deployment.
const stytchSsoConnections = (() => {
  try {
    return JSON.parse(import.meta.env.VITE_STYTCH_SSO_CONNECTIONS || '{}');
  } catch {
    return {};
  }
})();

// Which SSO connection ?action=sso-login should start. Bare = this deployment's
// own org; ?org= targets another it admits. The map IS the allowlist, so an
// unknown slug returns '' and the caller must render the login page instead —
// also the pre-SSO state where a first admin needs the password form.
export function resolveSsoConnectionId(orgSlug) {
  if (AUTH_PROVIDER !== 'stytch' || !stytch) return '';
  if (!orgSlug) return stytchConnectionId || '';

  // Own-property + string check, not a bare lookup: '__proto__' resolves to
  // Object.prototype and 'constructor' to a function, both truthy, so a bare
  // lookup would hand signIn a non-string and strand the user on a cancelled
  // navigation with no login form.
  if (!Object.hasOwn(stytchSsoConnections, orgSlug)) return '';
  const connectionId = stytchSsoConnections[orgSlug];
  return typeof connectionId === 'string' ? connectionId : '';
}

// Every SSO connection this deployment offers a one-click login for, own org
// first — LoginView renders one button per entry (CPRD-5849). isPrimary marks
// the deployment's own connection; the rest are admitted orgs (Revology staff
// on a customer instance) and get labeled by slug. Legitimately empty before a
// customer configures SSO — the caller renders only the breakglass widget then.
export function listSsoConnections() {
  if (AUTH_PROVIDER !== 'stytch' || !stytch) return [];
  return Object.entries(stytchSsoConnections)
    .filter(
      ([, connectionId]) =>
        typeof connectionId === 'string' && connectionId !== ''
    )
    .map(([orgSlug, connectionId]) => ({
      orgSlug,
      connectionId,
      isPrimary: connectionId === stytchConnectionId,
    }))
    .sort((a, b) => Number(b.isPrimary) - Number(a.isPrimary));
}

// Runtime-resolved connections (CPRD-5849): auxo-api asks Stytch live, so a
// connection configured moments ago (a new customer's first admin) shows a
// button on the very next /login load — no frontend deploy. Build-time values
// are the fallback: endpoint unset (local dev, Cognito builds), slow (>2s),
// failing, or malformed. An empty list from the server is truth, not failure —
// that's the pre-SSO window where only the breakglass widget belongs. Kept a
// bare GET with no custom headers so the browser caches it cheaply and skips
// the CORS preflight round-trip.
export async function fetchSsoConnections() {
  if (AUTH_PROVIDER !== 'stytch' || !stytch || !stytchSsoConnectionsUrl) {
    return listSsoConnections();
  }

  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 2000);
  try {
    const response = await fetch(stytchSsoConnectionsUrl, {
      signal: controller.signal,
    });
    if (!response.ok) {
      throw new Error(`Error: ${response.status} ${response.statusText}`);
    }
    const { data } = await response.json();
    // Same paranoia as the env map in resolveSsoConnectionId — this is
    // network-sourced data, so every field is type-checked before use.
    return (Array.isArray(data?.connections) ? data.connections : [])
      .filter(
        connection =>
          typeof connection?.org_slug === 'string' &&
          connection.org_slug !== '' &&
          typeof connection?.connection_id === 'string' &&
          connection.connection_id !== ''
      )
      .map(connection => ({
        orgSlug: connection.org_slug,
        connectionId: connection.connection_id,
        isPrimary: connection.is_primary === true,
      }));
  } catch (error) {
    console.error('Falling back to build-time SSO connections:', error);
    return listSsoConnections();
  } finally {
    clearTimeout(timeout);
  }
}

export async function signIn(connectionId = stytchConnectionId) {
  // Report failure so the caller can render the login page — otherwise the
  // router cancels the navigation and the user gets no form at all.
  if (!stytch || !connectionId) return false;

  // Clear any existing Stytch session and local tokens so we don't inherit a cached user
  clearTokens();
  try {
    if (stytch.session.getSync()) await stytch.session.revoke();
  } catch {
    /* session may already be expired */
  }

  // Redirects browser → Stytch → Okta → back to stytchRedirectUri
  // Callback will have ?stytch_token_type=sso&token=...
  stytch.sso.start({
    connection_id: connectionId,
    login_redirect_url: stytchRedirectUri,
    signup_redirect_url: stytchRedirectUri,
  });
  return true;
}

export async function signOut() {
  try {
    await stytch.session.revoke();
  } catch {
    /* session may already be expired or revoked */
  }
  clearTokens();
  window.location.replace('/logout');
}

// Cognito's own signOut has no knowledge that mountAdminPortalSSO may have
// bootstrapped a live Stytch session (e.g. via a shared browser). Called
// from the facade's signOut regardless of active provider so that session
// doesn't outlive this app's logout — no-op if none exists.
export async function revokeBootstrappedSession() {
  if (!stytch) return;
  try {
    if (stytch.session.getSync()) await stytch.session.revoke();
  } catch {
    /* session may already be expired or revoked */
  }
}

export async function getTokens(code, refresh_token) {
  return code ? _getStytchTokens(code) : _refreshStytchToken(refresh_token);
}

// ssoToken = the SSO token from ?token= in the Stytch callback URL
async function _getStytchTokens(ssoToken) {
  try {
    // Exchange SSO token → Stytch session. SDK handles the PKCE verifier automatically.
    const authResp = await stytch.sso.authenticate({
      sso_token: ssoToken,
      session_duration_minutes: 60,
    });

    // Send session to our backend for DB lookup + custom claim enrichment.
    const response = await fetch(stytchTokenUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        session_token: authResp.session_token,
      }),
    });

    if (!response.ok) {
      throw new Error(`Error: ${response.status} ${response.statusText}`);
    }

    const { data } = await response.json();
    setTokens(data.id_token, data.access_token, data.refresh_token);
  } catch (error) {
    console.error('Failed to get Stytch tokens:', error);
    throw error;
  }
}

// sessionToken = stored refresh_token (Stytch session token from prior auth)
async function _refreshStytchToken(sessionToken) {
  try {
    const response = await fetch(stytchTokenUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ session_token: sessionToken }),
    });

    if (!response.ok)
      throw new Error(`Error: ${response.status} ${response.statusText}`);
    const { data } = await response.json();
    setTokens(data.id_token, data.access_token, data.refresh_token);
  } catch (error) {
    console.error('Failed to refresh Stytch token:', error);
    throw error;
  }
}

// Our tokens live in per-tab sessionStorage, but the Stytch session cookie is
// shared across tabs — so a tab opened via "Open in new tab" can re-mint them.
export async function restoreSession() {
  if (AUTH_PROVIDER !== 'stytch' || !stytch) return false;

  const sessionToken = stytch.session.getTokens()?.session_token;
  if (!sessionToken) return false;

  try {
    await _refreshStytchToken(sessionToken);
    return true;
  } catch {
    return false;
  }
}

export async function isAuthenticated() {
  const token = sessionStorage.getItem('id_token');
  if (!token) return false;
  try {
    const { exp } = jwtDecode(token);
    return Date.now() < exp * 1000;
  } catch {
    return false;
  }
}

// The /login breakglass widget: Discovery flow carrying only the tier's
// fallback method (passwords on dev/test, email magic links on staging/prod).
// SSO is NOT offered here — the connection-scoped buttons above the widget
// (listSsoConnections + signIn) are the primary path, and the SSO discovery
// product is incompatible with the project's opaque-errors setting. Not the
// path the Okta tile takes (?action=sso-login skips this entirely) — this is a
// cold arrival, after a logout or from the unauthorized screen. Gated by the
// caller via VITE_STYTCH_MANUAL_LOGIN = 1.
export function mountManualLogin(elementId) {
  if (AUTH_PROVIDER !== 'stytch') return;

  // Password-reset-by-email links redirect back here with
  // ?stytch_token_type=multi_tenant_passwords&token=... — per Stytch's docs,
  // completing that flow requires mounting with authFlowType: PasswordReset
  // rather than our normal login config. Only where passwords are the
  // breakglass method; magic-link deployments never issue these links.
  const isPasswordResetRedirect =
    STYTCH_PASSWORDS_ENABLED &&
    new URL(window.location.href).searchParams.get('stytch_token_type') ===
      'multi_tenant_passwords';

  const config = isPasswordResetRedirect
    ? {
        authFlowType: AuthFlowType.PasswordReset,
        products: [B2BProducts.passwords],
        sessionOptions: { sessionDurationMinutes: 60 },
      }
    : {
        // No organizationSlug: Discovery resolves the organization from the
        // email instead of being told it up front, so one breakglass form can
        // serve both Revology staff and a client's own users. No
        // B2BProducts.sso: the SSO *discovery* product is rejected outright by
        // a project with opaque errors enabled
        // (sso_discovery_incompatible_with_sdk_config) — SSO runs through the
        // connection-scoped buttons (listSsoConnections/signIn) instead.
        authFlowType: AuthFlowType.Discovery,
        products: [
          STYTCH_PASSWORDS_ENABLED
            ? B2BProducts.passwords
            : B2BProducts.emailMagicLinks,
        ],
        sessionOptions: { sessionDurationMinutes: 60 },
        // ssoOptions WITHOUT the sso product, deliberately. When a discovered
        // org only permits SSO (auth_methods RESTRICTED ["sso"], non-breakglass
        // member), the widget hands the browser to connection-scoped sso.start
        // using these URLs; without them the public sso/start falls back to a
        // project default we never register and dies as raw
        // no_login_redirect_url JSON on api.stytch.com.
        ssoOptions: {
          loginRedirectURL: stytchRedirectUri,
          signupRedirectURL: stytchRedirectUri,
        },
        // The emailed link must land on a page with this widget mounted: the
        // widget completes discovery tokens itself, while the /status guard
        // only knows sso.authenticate. Discovery sends use discoveryRedirectURL
        // (loginRedirectURL covers the Organization-flow leg only).
        emailMagicLinksOptions: {
          loginRedirectURL: stytchRedirectUri,
          discoveryRedirectURL: `${window.location.origin}/login`,
        },
        // Password reset/signup links must redirect back to a page with this
        // same widget mounted (not the SSO-only /status callback) — see
        // isPasswordResetRedirect above for how we handle the return trip.
        //
        // discoveryRedirectURL is required, not optional — unset, Stytch falls
        // back to a project default we cannot have. All three intentionally
        // ignore BASE_URL: only unversioned paths are allowlisted.
        passwordOptions: {
          loginRedirectURL: `${window.location.origin}/login`,
          resetPasswordRedirectURL: `${window.location.origin}/login`,
          discoveryRedirectURL: `${window.location.origin}/login`,
        },
        // Skip the organization picker when the member belongs to exactly one
        // org, which is every user today — without this, a single-org member
        // would be shown a one-item list to choose from. ignore* keep that
        // shortcut in place even when Stytch could instead offer them a pending
        // invite or a JIT-provisionable org.
        directLoginForSingleMembership: {
          status: true,
          ignoreInvites: true,
          ignoreJitProvisioning: true,
        },
        // Members are provisioned by us (invite or SSO JIT), never self-serve.
        // This only hides the affordance — sign-up is actually off via the
        // project's basic.create_new_members and the org's
        // email_jit_provisioning. See CLAUDE.md ("Sign-up and password reset").
        disableCreateOrganization: true,
      };

  const loginProps = {
    client: stytch,
    config,
    presentation: {
      theme: {
        'container-width': '480px',
        primary: '#004b87', // only accepts hex values
        'primary-foreground': '#ffffff',
      },
      options: {
        logo: { url: auxoLeafUrl, alt: 'Auxo' },
        hideHeaderText: true,
      },
    },
    callbacks: {
      onEvent: message => {
        if (message.type === StytchEventType.AuthenticateFlowComplete) {
          completeManualLogin();
        }
      },
    },
  };
  const node = mount({ elementId, ...loginProps });
  node.render(loginProps);
}

async function completeManualLogin() {
  try {
    const tokens = stytch.session.getTokens();
    if (!tokens?.session_token) {
      throw new Error('No active Stytch session after manual login');
    }

    const response = await fetch(stytchTokenUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ session_token: tokens.session_token }),
    });

    if (!response.ok) {
      throw new Error(`Error: ${response.status} ${response.statusText}`);
    }

    const { data } = await response.json();
    setTokens(data.id_token, data.access_token, data.refresh_token);
    router.push('/status');
  } catch (error) {
    console.error('Failed to complete manual login:', error);
    // Same generic, non-leaking landing as the SSO-callback failure path —
    // without it the user is stranded on /login with no feedback at all.
    router.push({ name: 'unauthorized' });
  }
}

// When AUTH_PROVIDER=stytch
//   is already Stytch-session-authenticated from normal login.
// When AUTH_PROVIDER=cognito
//   no Stytch session exists yet, so we bootstrap one by
//   exchanging the caller's already-verified Cognito id_token for a Stytch
//   session via a Trusted Auth Token profile
export async function mountAdminPortalSSO(elementId) {
  if (!stytch) return; // Stytch not onboarded for this client at all

  if (AUTH_PROVIDER !== 'stytch' && !stytch.session.getSync()) {
    const cognitoIdToken = sessionStorage.getItem('id_token');
    if (!cognitoIdToken || !stytchTrustedAuthProfileId) return;

    try {
      await stytch.session.attest({
        profile_id: stytchTrustedAuthProfileId,
        organization_id: stytchOrganizationId,
        token: cognitoIdToken,
        session_duration_minutes: 60,
      });
    } catch (error) {
      console.error('Failed to bootstrap Stytch admin session:', error);
      return;
    }
  }

  mountAdminPortalSSOWidget({ client: stytch, element: elementId });
}
